import React, { memo, useMemo } from 'react'
import block from 'bem-cn'

import { Cell } from '../Cell'
import { HeadCell } from '../HeadCell'
import { ColumnAlignVariants, ColumnFixedVariants, ColumnType, DefaultDataType } from '../types'

import './Row.scss'

const b = block('table-row')

export enum RowVariants {
  HEAD_ROW = 'head',
  ROW = 'row',
}

type Props<D> = {
  columns: ColumnType<D>[]
  item?: DefaultDataType<D>
  className?: string
  variant?: RowVariants
  hasScroll?: boolean
  onRowClick?: (item: DefaultDataType<D>) => void
}
// Типизация компонента с memo
const RowComponent = <D,>({ className, onRowClick, item, hasScroll, columns, variant = RowVariants.ROW }: Props<D>) => {
  const renderedColumns = useMemo(() => {
    const columnsLeftFixed = columns
      .filter(col => col.fixed === ColumnFixedVariants.LEFT)
      .map(col => ({ ...col, left: 0 }))
      .map((col, i, arr) => {
        const prev = arr[i - 1]
        const prevWidth = prev?.width || prev?.minWidth || prev?.maxWidth

        return {
          ...col,
          left: i === 0 ? 0 : prev?.left + (Number(prevWidth) || 0),
          width: arr[i].width,
        }
      })
      .reverse()
    const columnsRightFixed = columns
      .filter(col => col.fixed === ColumnFixedVariants.RIGHT)
      .map(col => ({ ...col, right: 0 }))
      .reverse()
      .map((col, i, arr) => {
        const prev = arr[i - 1]
        const prevWidth = prev?.width || prev?.minWidth || prev?.maxWidth

        return {
          ...col,
          right: i === 0 ? 0 : prev?.right + (Number(prevWidth) || 0),
          width: arr[i].width,
        }
      })
      .reverse()

    return columns.map((col, i) => {
      const column: ColumnType<D> & { left?: number; right?: number } =
        columnsLeftFixed.find(colLeft => colLeft.key === col.key) ||
        columnsRightFixed.find(colRight => colRight.key === col.key) ||
        col
      const leftIndex = columnsLeftFixed.findIndex(colLeft => colLeft.key === column.key)
      const index = leftIndex === -1 ? columnsRightFixed.findIndex(colRight => colRight.key === column.key) : leftIndex

      const withBorderAndShadow = column.fixed && hasScroll ? index === 0 : undefined
      const left = column.fixed !== ColumnFixedVariants.LEFT ? undefined : column.left
      const right = column.fixed !== ColumnFixedVariants.RIGHT ? undefined : column.right
      const firstCol = i === 0
      const lastCol = i === columns.length - 1
      let { align } = col

      if (!align) {
        if (firstCol) {
          align = ColumnAlignVariants.LEFT
        }
      }

      if (variant === RowVariants.HEAD_ROW) {
        return (
          <HeadCell
            key={String(column.key)}
            className={b('header-cell')}
            sortable={column.sortable}
            type={column.variant}
            fixed={column.fixed}
            left={left}
            align={align}
            isFirst={firstCol}
            isLast={lastCol}
            right={right}
            withBorder={withBorderAndShadow}
            withShadow={withBorderAndShadow}>
            <div className={b('header-text')}>{column.title ? column.title : column.key}</div>
          </HeadCell>
        )
      }

      if (variant === RowVariants.ROW && item) {
        return (
          <Cell
            key={item.id + String(column.key)}
            type={column.variant}
            fixed={column.fixed}
            align={align}
            left={left}
            isFirst={firstCol}
            isLast={lastCol}
            right={right}
            withBorder={withBorderAndShadow}
            withShadow={withBorderAndShadow}>
            {column.render
              ? column.render(item[column.key as keyof DefaultDataType<D>], item)
              : item[column.key as keyof DefaultDataType<D>]}
          </Cell>
        )
      }

      return null
    })
  }, [columns, hasScroll, variant, item])

  const clickable = Boolean(onRowClick)

  return (
    <tr className={b('wrapper').mix(className, clickable && 'clickable')} onClick={() => item && onRowClick?.(item)}>
      {renderedColumns}
    </tr>
  )
}

// Оборачиваем компонент в memo и типизируем
export const Row = memo(RowComponent) as typeof RowComponent & { displayName: string }
Row.displayName = 'Row'
